import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import logo from '../assets/Nimbus_logo_only.png';
import { colors } from '../styles/colors';

function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setIsLoading(true);

    try {
      const response = await fetch('/.netlify/functions/requestPasswordReset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(data.message);
        setEmail(''); // Réinitialiser le champ
      } else {
        setError(data.error || 'Une erreur est survenue');
      }
    } catch (err) {
      console.error('Erreur lors de la demande de réinitialisation:', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* Logo */}
      <div style={styles.logoContainer}>
        <img src={logo} alt="Nimbus Logo" style={styles.logo} />
      </div>

      {/* Formulaire */}
      <div style={styles.formContainer}>
        <h1 style={styles.title}>Mot de passe oublié ?</h1>
        <p style={styles.subtitle}>
          Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
        </p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label htmlFor="email" style={styles.label}>
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              style={styles.input}
              disabled={isLoading}
              placeholder="votre.email@exemple.com"
            />
          </div>

          {error && (
            <div style={styles.errorMessage}>
              {error}
            </div>
          )}

          {message && (
            <div style={styles.successMessage}>
              {message}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            style={{
              ...styles.button,
              opacity: isLoading ? 0.6 : 1,
              cursor: isLoading ? 'not-allowed' : 'pointer'
            }}
          >
            {isLoading ? 'Envoi en cours...' : 'Envoyer le lien'}
          </button>
        </form>

        {/* Lien retour vers login */}
        <Link to="/login" style={styles.backLink}>
          <ArrowLeft size={16} style={{ marginRight: '0.5rem' }} />
          Retour à la connexion
        </Link>
      </div>

      {/* Styles CSS */}
      <style>
        {`
          input:focus {
            outline: none;
            border-color: ${colors.primary.teal};
            box-shadow: 0 0 0 3px ${colors.primary.teal}20;
          }

          button:hover:not(:disabled) {
            background-color: ${colors.primary.teal};
            opacity: 0.9;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }

          button:active:not(:disabled) {
            transform: translateY(0);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }
        `}
      </style>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.secondary.lightGray,
    fontFamily: '"Noto Sans", sans-serif',
    padding: '2rem'
  },
  logoContainer: {
    marginBottom: '2rem'
  },
  logo: {
    width: '120px',
    height: 'auto',
    display: 'block'
  },
  formContainer: {
    backgroundColor: colors.text.white,
    padding: '3rem',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    width: '100%',
    maxWidth: '450px'
  },
  title: {
    fontSize: '1.875rem',
    fontWeight: '700',
    color: colors.primary.gray,
    textAlign: 'center',
    marginBottom: '1rem',
    marginTop: 0
  },
  subtitle: {
    fontSize: '0.9375rem',
    color: colors.secondary.mediumGray,
    textAlign: 'center',
    marginBottom: '2rem',
    lineHeight: '1.5'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  },
  label: {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: colors.primary.gray
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    fontSize: '1rem',
    border: `1px solid ${colors.secondary.mediumGray}`,
    borderRadius: '6px',
    transition: 'all 0.2s ease',
    fontFamily: '"Noto Sans", sans-serif',
    boxSizing: 'border-box'
  },
  button: {
    width: '100%',
    padding: '0.875rem',
    fontSize: '1rem',
    fontWeight: '600',
    color: colors.text.white,
    backgroundColor: colors.primary.teal,
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: '"Noto Sans", sans-serif',
    marginTop: '0.5rem'
  },
  errorMessage: {
    padding: '0.75rem',
    backgroundColor: '#fee',
    color: '#c33',
    borderRadius: '6px',
    fontSize: '0.875rem',
    textAlign: 'center',
    border: '1px solid #fcc'
  },
  successMessage: {
    padding: '0.75rem',
    backgroundColor: '#d1fae5',
    color: '#065f46',
    borderRadius: '6px',
    fontSize: '0.875rem',
    textAlign: 'center',
    border: '1px solid #10b981'
  },
  backLink: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '1.5rem',
    color: colors.primary.teal,
    textDecoration: 'none',
    fontSize: '0.9375rem',
    fontWeight: '500',
    transition: 'opacity 0.2s',
    ':hover': {
      opacity: 0.8
    }
  }
};

export default ForgotPasswordPage;
